/* global window */
import PropTypes from 'prop-types';
import { injectScript } from '@atc/dom-fns';
import React, { useCallback, useEffect, useRef } from 'react';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';
import { deprecate } from 'react-is-deprecated';
import { useDispatch, useSelector } from 'react-redux';
import { useInView } from 'react-intersection-observer';
import { v5 as uuidv5 } from 'uuid';
import hasDom from 'has-dom';

import { eventBus } from '@atc/cai-event-bus';
import {
    WALLET_GET_DEAL_DETAILS_WITH_POLLING_REQUEST,
    WALLET_CREATE_ID_REQUEST,
    WALLET_CREATE_ID_COMPLETED,
    WALLET_OPEN_SDP_REQUEST,
    UDS_OPEN_SDP_REQUEST,
} from 'wallet-manager';

import { ratesLabels } from 'axl-config';

import { useDevice } from '@atc/react-device';

import { sendClick } from 'reaxl-analytics';

import {
    creditAppSubmit,
    creditAppGetPreQualifiedClick,
    creditPreApprovalClick,
    drDealSubmissions,
    editDealerOfferCTAClick,
    myWalletInteractionClick,
    ndsCalcCtaClick,
    ndsLendingOptionsCTAClick,
    sdpRequestAppointment,
    submitOfferButtonClick,
    startCreditApplication,
    testDriveSubmit,
    textLinkDealClick,
    valueYourVehicleClick,
    vehicleProtectionClick,
} from 'reaxl-analytics-handlers';

import { useFeatures } from '@atc/features';
import { Button, Chip, LinkIcon } from 'reaxl';
import { buildKBBTradeInObject } from './utilities';

const MAX_RETRIES = 20;

/**
 * DigitalRetailOverlayCTA is a component that renders a call-to-action button for digital retail functionality.
 * It handles different UI contexts and integrates with Accelerate, My Wallet, and other systems.
 * The component manages wallet IDs, deal creation, and event tracking for various user actions.
 *
 * Digital Retail 3.0 Platform documentation: https://coxautoinc.sharepoint.com/sites/DigitalRetail3_0Platform/SitePages/Cox-Auto-Digital-Retailing---Shopper-Detail-Page-Widget-Integration.aspx
 *
 * @component
 * @param {Object} props - Component props
 * @param {(string|number)} [props.listedPrice] - The listed price of the vehicle
 * @param {Object} [props.listing={}] - The vehicle listing object containing details like VIN, make, model, etc.
 * @param {Object} [props.paymentsInfo={}] - Information about payments, including active tab and interactions
 * @param {Object} [props.drOverlayHooks={}] - Hooks for Digital Retail overlay events
 * @param {Function} [props.drOverlayHooks.onTrack] - Called when tracking events occur
 * @param {Function} [props.drOverlayHooks.onLoaded] - Called when the DR overlay is loaded
 * @param {Function} [props.drOverlayHooks.onHidden] - Called when the DR overlay is hidden
 * @param {string} [props.brand] - The brand name (deprecated in favor of accelerate.sponsor brand config)
 * @param {Function} [props.onClick=()=>{}] - Function to call when the button is clicked
 * @param {string} [props.buttonStyle] - Style of the button (primary, secondary, text, etc.)
 * @param {string} [props.buttonText] - Text to display on the button
 * @param {string} [props.buttonSize] - Size of the button (large, medium, small)
 * @param {string} [props.buttonClassName=''] - Additional CSS class for the button
 * @param {Function} [props.onError=()=>{}] - Function to call when an error occurs
 * @param {Object} [props.paymentsDuck] - Redux duck for payments functionality
 * @param {Object} [props.dealsDuck] - Redux duck for deals functionality
 * @param {string} [props.route] - The route to navigate to when the button is clicked
 * @param {string} [props.uiContext='default'] - The UI context where the component is being used
 * @param {string} [props.placement] - Where the component is placed in the UI
 * @param {boolean} [props.isReceiptView] - Whether the component is being shown in a receipt view
 * @param {string} [props.parent='default'] - The parent component or context
 * @param {Object} [props.userDuck] - Redux duck for user functionality
 * @param {boolean} [props.hideSendOfferCTA=false] - Whether to hide the Send Offer CTA
 * @returns {React.Component} The Digital Retail Overlay CTA component
 */
function DigitalRetailOverlayCTA({
    brand: deprecatedBrand,
    buttonStyle,
    buttonSize,
    buttonText,
    buttonClassName = '',
    drOverlayHooks = {
        onTrack: () => {},
        onLoaded: () => {},
        onHidden: () => {},
    },
    hideSendOfferCTA = false,
    listedPrice,
    listing = {},
    onError = () => {},
    onClick = () => {},
    paymentsDuck,
    dealsDuck,
    paymentsInfo = {},
    route,
    uiContext = 'default',
    placement,
    isReceiptView,
    parent = 'default',
    userDuck,
    glyph = 'chevron-right',
}) {
    // Do NOT remove direct access to state for payments because paymentsDuck is not always passed through.
    const paymentsSelector = paymentsDuck ? paymentsDuck.selectors.getDuckState : (state) => state.payments;
    const payments = useSelector(paymentsSelector) || {};

    const isPenciledVdpListing =
        useSelector((state) => paymentsDuck?.selectors?.isPenciledVdpListing(state, listing?.id)) || false;

    const {
        accelerate: [, { sdp_script: sdpScript, sponsor = 'atc' }],
        my_wallet: [, { enable_deals: enableDeals }],
        native_sdp: [enableNativeSdp],
    } = useFeatures(['accelerate', 'my_wallet', 'nds_calc', 'native_sdp']);

    let brand = sponsor;
    if (deprecatedBrand) {
        brand = deprecatedBrand;
    }

    const dispatch = useDispatch();
    const device = useDevice();
    const isXs = _get(device, 'is.xs', true);

    const hasDomNoScript = hasDom() && !window.accelerate;
    const { activeTab = '', interactions = {}, financePayment, leasePayment } = paymentsInfo;

    const { paymentEmphasis = false } = interactions;

    // Cache uiContext as bools
    const isSDPlead = uiContext === 'lead';
    const isCreditApp = uiContext === 'creditApp';
    const isCreditAppNextSteps = uiContext === 'nextSteps';
    const isPaymentCard = uiContext === 'paymentCard';
    const isTestDrive = uiContext === 'testDrive';
    const isNbaTestDrive = uiContext === 'nbaTestDrive';
    const isDealPanel = uiContext === 'dealPanel';
    const isNbaValueYourVehicle = uiContext === 'nbaValueYourVehicle';
    const isIncentives = uiContext === 'incentives';
    const isStickyHeader = uiContext === 'stickyHeader';
    const isShoppingAssistant = uiContext === 'shoppingAssistant';

    const userZip = useSelector((state) => _get(state, 'location.zip'), _isEqual);
    const zip = paymentsInfo?.zip || userZip || listing?.zip || listing?.owner?.location?.zip;
    const {
        firstName = '',
        lastName = '',
        email = '',
        phone = '',
    } = useSelector((state) => userDuck?.selectors?.getSelectedUserProfile(state)) || {};

    // Set up refs
    const scriptInjected = useRef(false);
    const [ref, inView] = useInView({
        rootMargin: '0px 0px',
        triggerOnce: true,
    });

    // Set up `lead` configuration
    switch (uiContext) {
        case 'lead': // Accelerate lead form CTA in nds
            buttonStyle = buttonStyle || 'secondary';
            route = 'shopperLeadForm';
            buttonText = 'Send Offer';
            break;
        case 'creditApp': // Accelerate credit app CTA in nds
        case 'nextSteps': // Accelerate credit app CTA in email confirmation
        case 'stickyHeader': // Accelerate credit app CTA in sticky header
            route = 'credit';
            break;
        case 'paymentCard':
            route = route || 'credit';
            buttonText = buttonText || 'Apply Now';
            break;
        case 'protectionList':
            buttonStyle = buttonStyle || 'text-decorated';
            buttonText = buttonText || 'Explore vehicle protection options';
            route = 'protectionList';
            break;
        case 'testDrive': // Accelerate scheduleTestDrive CTA in lead form on page
            route = 'testDrive';
            buttonText = 'Request Appointment';
            break;
        case 'nbaTestDrive': // Accelerate scheduleTestDrive CTA in Next Best Action
            route = 'testDrive';
            buttonText = buttonText || 'Schedule a Test Drive';
            break;
        case 'dealPanel':
            route = route || 'paymentOptions';
            buttonText = enableNativeSdp ? buttonText || 'Edit' : 'Continue Online';
            buttonStyle = buttonStyle || 'text';
            break;
        case 'nbaValueYourVehicle':
            route = route || 'tradeInManualEntry';
            buttonText = buttonText || 'Value Your Vehicle';
            break;
        case 'shoppingAssistant':
            route = route || 'testDrive';
            buttonText = buttonText || 'Schedule test drive';
            break;
        default:
            buttonText = buttonText || 'Review Offer Details';
            route = route || 'dashboard';
            break;
    }

    // Set up default button style if it wasn't provided or set by uiContext
    if (!buttonStyle) {
        buttonStyle = 'primary';
    }

    const injectSDPScript = () => {
        if (scriptInjected.current || window?.accelerate) {
            return;
        }
        if (hasDomNoScript) {
            injectScript(sdpScript || 'https://shop.dealer.com/widget.sdp.js')
                .catch((err) => {
                    // eslint-disable-next-line no-console
                    console.warn('Error injecting accelerate digital retail script: ', err);
                    if (window && window.newrelic) {
                        window.newrelic.addPageAction('ThirdPartyError', {
                            thirdPartyName: 'Accelerate',
                            component: 'DigitalRetailOverlayCTA',
                            message: 'Error injecting accelerate digital retail script',
                            status: err?.status || 'unknown',
                            url: window.location.href,
                            error: err,
                        });
                    }
                })
                .then(() => {
                    scriptInjected.current = true;
                });
        }
    };

    const openSdpWithEventBus = !enableNativeSdp && uiContext === 'dealPanel';

    useEffect(() => {
        if (!openSdpWithEventBus && inView) {
            injectSDPScript();
        }
        return undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inView]);

    const {
        id: existingDealId,
        testDrive: existingDealTestDrive,
        vehicle: dealVehicle,
    } = useSelector((state) => dealsDuck?.selectors?.getDealByListingVin(state, listing?.vin)) || {};

    const walletIdFromDuck = useSelector((state) => paymentsDuck?.selectors?.getWalletId(state));

    const isDealXgIdAvailable = enableDeals && !!existingDealId;

    let walletId = payments?.walletId ?? paymentsInfo?.walletId;

    const errorTracing = {
        id: listing.id,
        vin: listing?.vin,
        emailFormDisabled: hideSendOfferCTA,
        route,
        component: 'DigitalRetailOverlayCTA',
        brand: brand || 'ddc',
    };

    function updateWalletId(data = '') {
        if (data.length) {
            walletId = data;
        }
    }

    const listenForWalletId = () => {
        if (!walletId && !!localStorage.getItem('WALLET_ID')) {
            walletId = localStorage.getItem('WALLET_ID');
        }
        eventBus.on(WALLET_CREATE_ID_COMPLETED, updateWalletId);

        return () => {
            eventBus.off(WALLET_CREATE_ID_COMPLETED, updateWalletId);
        };
    };

    const handleCreateWalletId = async () => {
        await eventBus.emit(WALLET_CREATE_ID_REQUEST);
    };

    const onDRLoaded = (errorInfo, offer) => {
        // Hash offer object for error tracing
        errorTracing.hash = offer ? uuidv5 && uuidv5(JSON.stringify(offer), uuidv5?.URL) : ' ';

        if (errorInfo) {
            onError();
            if (!isDealPanel) {
                dispatch(paymentsDuck.creators.updateMyWalletInteractions({ useFallbackLead: true }));
            }
            if (window?.newrelic?.addPageAction) {
                window.newrelic.addPageAction('ThirdPartyError', { ...errorTracing, message: errorInfo });
            }
        } else {
            // temporarily debug number of clicks vs. errors vs. loads since analytics data isn't matching.
            if (window?.newrelic?.addPageAction) {
                window.newrelic.addPageAction('ThirdPartyAction', { ...errorTracing, message: `SDP Loaded ${route}` });
            }
            drOverlayHooks.onLoaded();
        }
    };

    const onDRTrack = (name, action, data, selectedCriteria) => {
        const {
            creditScoreHigh,
            creditScoreLow,
            dealType,
            downPayment,
            financeTermMonths,
            leaseTermMonths,
            annualMiles,
            dealXgId,
        } = data;
        const { leaseTermOptions = [], leaseMileageOptions = [] } = selectedCriteria || {};
        const deal = {};

        if (creditScoreLow && creditScoreHigh) {
            deal.creditType = Object.keys(ratesLabels).find(
                (key) => ratesLabels[key].subLabel === `(${creditScoreLow} - ${creditScoreHigh})`
            );
        }

        if (downPayment) {
            deal.downPayment = downPayment;
        }

        /* Currently NDS support only 36, 48, 60, 72 terms ignoring other terms from DR */
        // TODO: Consume the finance terms from UDS config instead of hardcoding
        if ([36, 48, 60, 72].includes(financeTermMonths)) {
            deal.loanTerm = financeTermMonths;
        }

        /* Track SDP update for lease mileage change and update UDS */
        if (annualMiles && leaseMileageOptions.length > 0 && leaseMileageOptions.includes(annualMiles)) {
            deal.leaseMileageRange = annualMiles;
        }

        /* Track SDP update for lease term change and update UDS */
        if (leaseTermMonths && leaseTermOptions.length > 0 && leaseTermOptions.includes(leaseTermMonths)) {
            deal.leaseTerm = leaseTermMonths;
        }

        switch (name) {
            case 'PROFILE_UPDATED':
                dispatch(
                    paymentsDuck.creators.updatePaymentsInfo({
                        ...paymentsInfo,
                        ...deal,
                    })
                );
                break;
            case 'DR_PAYMENT_TYPE_CHANGED':
                dispatch(
                    paymentsDuck.creators.updatePaymentsInfo({
                        ...paymentsInfo,
                        dealType,
                    })
                );
                break;
            case 'DR_TRADEIN_ESTIMATE_RECEIVED':
                dispatch(
                    paymentsDuck.creators.updatePaymentsInfo({
                        ...paymentsInfo,
                        tradeIn: {
                            ...paymentsInfo.tradeIn,
                            ...buildKBBTradeInObject(data),
                        },
                    })
                );
                break;
            case 'DR_DXG_ID_VERSION':
                if (enableDeals && dealXgId) {
                    // Added logging for further testing on deal creation defect. See US985582
                    // eslint-disable-next-line no-console
                    console.log('Deal has been created with ID: ', dealXgId);
                    if (window && window.newrelic) {
                        window.newrelic.addPageAction('ThirdPartyAction', {
                            ...errorTracing,
                            message: `Deal returned from SDP ${route}`,
                        });
                    }

                    eventBus.emit(WALLET_GET_DEAL_DETAILS_WITH_POLLING_REQUEST, {
                        walletId: walletIdFromDuck ?? walletId,
                        dealXgId,
                    });

                    if (uiContext !== 'dealPanel') {
                        sendClick(drDealSubmissions, null, { uiContext, listingId: listing?.id });
                    }
                }
                break;
            case 'DR_CREDIT_APP_SUCCESS_DISPLAYED':
                eventBus.emit(WALLET_GET_DEAL_DETAILS_WITH_POLLING_REQUEST, {
                    walletId: walletIdFromDuck ?? walletId,
                    dealXgId,
                });

                sendClick(creditAppSubmit, null, {
                    ...data,
                    creditAppType: 'accelerate',
                    listingId: listing?.id,
                });
                break;
            case 'DR_TEST_DRIVE_REQUESTED':
                if (existingDealId) {
                    // Update the test drive section of this deals details
                    dispatch(
                        dealsDuck?.creators?.updateDealDetails(existingDealId, {
                            testDrive: {
                                ...(existingDealTestDrive && existingDealTestDrive),
                                // Set this to today to update the badge in the UI, the next fetch of dealDetails will correct this date field in redux
                                scheduledDate: `${new Date().toUTCString()}`,
                            },
                        })
                    );
                }
                sendClick(testDriveSubmit, null, {
                    ...data,
                    inventoryId: listing?.id,
                });
                break;
            case 'DR_OFFER_SUBMISSION_CLICKED':
                sendClick(submitOfferButtonClick, null, data);
                break;
            default:
        }

        drOverlayHooks.onTrack(name, action, data);
    };

    const onDRHidden = () => {
        if (existingDealId) {
            eventBus.emit(WALLET_GET_DEAL_DETAILS_WITH_POLLING_REQUEST, {
                walletId: walletIdFromDuck ?? walletId,
                dealXgId: existingDealId,
            });
        }
        drOverlayHooks.onHidden();
    };

    const hooks = {
        onDRTrack,
        onDRLoaded,
        onDRHidden,
    };

    const pixall = {
        referringSource: isTestDrive ? 'CS VDP' : 'My Wallet NDS',
        initiator: 'shopper',
    };

    const handleDealPanelCTAClick = useCallback(
        (event) => {
            switch (route) {
                case 'credit':
                    sendClick(creditPreApprovalClick, event, {
                        text: buttonText,
                        partnerType: buttonText === 'Follow-up' ? '' : 'amd',
                        isReceiptView,
                        inventoryId: listing?.id,
                    });
                    break;
                case 'testDrive':
                    sendClick(textLinkDealClick, event, {
                        inventoryId: listing?.id,
                        text: buttonText,
                        isTestDrive: true,
                    });
                    break;
                case 'protectionList':
                    sendClick(textLinkDealClick, event, { text: buttonText, inventoryId: listing?.id });
                    break;
                case 'paymentOptions':
                case 'tradeInManualEntry':
                    if (buttonText === 'Modify Your Offer') {
                        sendClick(editDealerOfferCTAClick, event, { isReceiptView, inventoryId: listing?.id });
                    } else if (isReceiptView && !!placement) {
                        sendClick(textLinkDealClick, event, {
                            text: buttonText,
                            placement,
                            inventoryId: listing?.id,
                        });
                    }
                    break;
                default:
            }
        },
        [buttonText, isReceiptView, listing?.id, placement, route]
    );

    const handleClickTagging = useCallback(
        (event) => {
            event.stopPropagation();
            const { type = '', id } = listing;
            switch (uiContext) {
                case 'testDrive':
                    sendClick(sdpRequestAppointment, event, { listingId: id, listingType: type });
                    break;
                case 'nbaTestDrive':
                    // Analytics tagging for NBA Test Drive option is handled in its parent component (EmailFormNextStepsContainer)
                    // TODO: move it into this file
                    break;
                case 'nbaValueYourVehicle':
                    sendClick(valueYourVehicleClick, event, { listingId: id, placement: 'nba' });
                    break;
                case 'nextSteps':
                    sendClick(startCreditApplication, event, { listingId: id, partner: 'amd' });
                    break;
                case 'dealPanel':
                    handleDealPanelCTAClick(event); // Handle tags specific to deal dashboard
                    break;
                case 'paymentCard':
                    sendClick(creditAppGetPreQualifiedClick, event, {
                        listingId: id,
                        partner: 'amd',
                        section: 'price-card',
                    });
                    break;
                case 'protectionList':
                    sendClick(vehicleProtectionClick, event, { listingId: id, listingType: type });
                    break;
                default:
                    sendClick(ndsCalcCtaClick, event, {
                        listingType: type,
                        listingId: id,
                        cmp: isSDPlead ? 'snd_dlr_ofr' : 'cnd_dl_onl_amd',
                        co_txt_url: isSDPlead ? `Send Offer ${activeTab}` : `${buttonText} ${activeTab}`,
                        pageType: 'vdp',
                        eventResult: isSDPlead ? 'cta::send-offer' : 'cta::next-continue-online',
                        isSDPlead,
                        buttonText,
                        paymentsDuck,
                        activeTab,
                        financePayment,
                        leasePayment,
                        downPayment: paymentsInfo?.downPayment,
                    });
                    break;
            }
        },
        [
            listing,
            uiContext,
            handleDealPanelCTAClick,
            isSDPlead,
            activeTab,
            buttonText,
            paymentsDuck,
            financePayment,
            leasePayment,
            paymentsInfo?.downPayment,
        ]
    );

    const handleAMDPreQualClickTagging = useCallback(
        (event) => {
            event.stopPropagation();
            sendClick(ndsLendingOptionsCTAClick, event, {
                listingId: listing?.id,
                placement: isStickyHeader ? 'sticky-header' : undefined,
                paymentsDuck,
                activeTab,
            });
        },
        [listing?.id, isStickyHeader, paymentsDuck, activeTab]
    );

    const handleSdpOpen = async (event) => {
        // NOTE: Syndicated sites will need to bypass loading the sdp script, instead we send the open SDP event through the eventBus
        let sdpRoute = route;

        if (openSdpWithEventBus) {
            if (route === 'tradeInManual' || route === 'tradeInManualEntry') {
                sdpRoute = 'tradeInSearch';
            }
            eventBus.emit(WALLET_OPEN_SDP_REQUEST, {
                route: sdpRoute,
                vdpUrl: dealVehicle?.vehicle?.vdpUrl,
                dealXgId: existingDealId,
            });
        } else {
            // debug number of clicks vs. errors vs. loads since analytics data isn't matching.
            if (window?.newrelic?.addPageAction) {
                window.newrelic.addPageAction('ThirdPartyAction', {
                    ...errorTracing,
                    message: `SDP requested ${route}`,
                });
            }

            onClick(event);
            if (!paymentEmphasis) {
                await dispatch(
                    paymentsDuck.creators.updatePaymentsInfo({
                        ...paymentsInfo,
                        interactions: {
                            ...paymentsInfo.interactions,
                            hasUpdatedNDSCalc: true,
                            paymentEmphasis: true,
                            disablePaymentsPopover: true,
                        },
                    })
                );
            }

            if (uiContext !== 'testDrive' && !isPenciledVdpListing) {
                sendClick(myWalletInteractionClick, event, {
                    clickType: listing?.clickType,
                    isNDS: parent === 'nds',
                    paymentsDuck,
                });

                if (paymentsDuck) {
                    dispatch(paymentsDuck.creators.updatePenciledVdpListings(listing?.id));
                    dispatch(paymentsDuck.creators.updatePenciledLatestPageId('vdp'));
                }
            }

            if (!isCreditApp && !isStickyHeader) {
                handleClickTagging(event);
            } else {
                handleAMDPreQualClickTagging(event);
            }

            const {
                vin,
                paymentServices,
                pricingDetail,
                images,
                specifications,
                urls,
                mileage,
                type,
                listingType,
                condition,
                stockNumber,
                year,
                id,
                styleId,
                make,
                model,
                trim,
            } = listing;

            eventBus.emit(UDS_OPEN_SDP_REQUEST, {
                route: sdpRoute,
                isMobile: isXs ? 'yes' : 'no',
                hooks,
                pixall,
                brand,
                shopper: {
                    firstName,
                    lastName,
                    email,
                    phone: String(phone).replace(/\D/g, ''),
                    zip,
                },
                listing: {
                    // Passing only subset of listing object
                    vin,
                    paymentServices,
                    pricingDetail,
                    images,
                    specifications,
                    urls,
                    mileage,
                    type,
                    listingType,
                    condition,
                    stockNumber,
                    year,
                    id,
                    styleId,
                    make,
                    model,
                    trim,
                },
                listedPrice, // Need to pass it separate as it accounts for price coming from retail engine
                paymentsInfo,
            });
        }
    };

    const triggerOverlay = async (event) => {
        event.persist();

        let retryCount = 0;

        const retryWithDelay = () => {
            setTimeout(() => {
                listenForWalletId();
                retryCount += 1;
                if (!walletId && retryCount < MAX_RETRIES) {
                    // NR Logging to see if the 20 retries is needed
                    if ((retryCount === 6 || retryCount === 15) && !walletId && window && window.newrelic) {
                        window.newrelic.addPageAction('ThirdPartyError', {
                            ...errorTracing,
                            message: `walletId creation timed out after ${(retryCount * 500) / 1000}s`,
                        });
                    }

                    retryWithDelay();
                } else {
                    if (!walletId && window && window.newrelic) {
                        window.newrelic.addPageAction('ThirdPartyError', {
                            ...errorTracing,
                            message: 'walletId creation timed out',
                        });
                    }

                    handleSdpOpen(event);
                }
            }, 500);
        };

        if (!walletId && !isDealXgIdAvailable) {
            await handleCreateWalletId();
            retryWithDelay();
        } else {
            handleSdpOpen(event);
        }
    };

    return (
        <>
            {
                // Hidden anchor because button doesn't forward refs
                //  eslint-disable-next-line jsx-a11y/anchor-has-content, jsx-a11y/anchor-is-valid
                <a
                    ref={ref}
                    aria-hidden="true"
                    name={`digital-retail-overlay-cta-${route}`}
                />
            }

            {!(isCreditApp || isCreditAppNextSteps || isNbaTestDrive || isNbaValueYourVehicle) &&
                !isPaymentCard &&
                !isShoppingAssistant && (
                    <Button
                        block={!isDealPanel && !isIncentives && !isStickyHeader}
                        bsStyle={buttonStyle}
                        bsSize={buttonSize}
                        type="button"
                        onClick={triggerOverlay}
                        key="digital-retail-overlay-cta"
                        data-cmp="sdp-overlay-cta"
                        className={buttonClassName}
                    >
                        {buttonText}
                    </Button>
                )}

            {isCreditApp && (
                <LinkIcon
                    className="text-nowrap"
                    data-cmp="sdp-credit-app"
                    placement="after"
                    glyph={glyph}
                    onClick={triggerOverlay}
                    undecorated
                >
                    Apply for Financing
                </LinkIcon>
            )}

            {(isCreditAppNextSteps || isNbaTestDrive || isNbaValueYourVehicle) && (
                <Button
                    block
                    bsSize={buttonSize}
                    className="padding-horizontal-0"
                    data-cmp="sdp-credit-app"
                    onClick={triggerOverlay}
                >
                    {buttonText ?? 'Start a Credit Application'}
                </Button>
            )}

            {isPaymentCard && (
                <Button
                    bsStyle="text"
                    onClick={triggerOverlay}
                >
                    {buttonText}
                </Button>
            )}

            {isShoppingAssistant && (
                <Chip
                    label={buttonText || 'Schedule test drive'}
                    onClick={triggerOverlay}
                    uiContext="inquiry"
                />
            )}
        </>
    );
}

DigitalRetailOverlayCTA.propTypes /* remove-proptypes */ = {
    /** The listed price of the vehicle */
    listedPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    /** The vehicle listing object containing details like VIN, make, model, etc. */
    listing: PropTypes.object,
    /** Information about payments, including active tab and interactions */
    paymentsInfo: PropTypes.object,
    /** Hooks for Digital Retail overlay events */
    drOverlayHooks: PropTypes.shape({
        onHidden: PropTypes.func,
        onLoaded: PropTypes.func,
        onTrack: PropTypes.func,
    }),
    /** The brand name (deprecated in favor of accelerate.sponsor brand config) */
    brand: deprecate(
        PropTypes.string,
        "Use of prop 'brand' is deprecated in favor of accelerate.sponsor brand config."
    ),
    /** Function to call when the button is clicked */
    onClick: PropTypes.func,
    /** Style of the button (primary, secondary, text, etc.) */
    buttonStyle: PropTypes.string,
    /** Text to display on the button */
    buttonText: PropTypes.string,
    /** Size of the button */
    buttonSize: PropTypes.oneOf(['large', 'medium', 'small']),
    /** Additional CSS class for the button */
    buttonClassName: PropTypes.string,
    /** Function to call when an error occurs */
    onError: PropTypes.func,
    /** Redux duck for payments functionality */
    paymentsDuck: PropTypes.object,
    /** Redux duck for deals functionality */
    dealsDuck: PropTypes.object,
    /** The route to navigate to when the button is clicked */
    route: PropTypes.string,
    /** The UI context where the component is being used */
    uiContext: PropTypes.oneOf([
        'default',
        'lead',
        'creditApp',
        'testDrive',
        'nbaTestDrive',
        'nbaValueYourVehicle',
        'paymentCard',
        'dealPanel',
        'nextSteps',
        'protectionList',
        'incentives',
        'stickyHeader',
        'shoppingAssistant',
    ]),
    /** Where the component is placed in the UI */
    placement: PropTypes.string,
    /** Whether the component is being shown in a receipt view */
    isReceiptView: PropTypes.bool,
    /** The parent component or context */
    parent: PropTypes.string,
    /** Redux duck for user functionality */
    userDuck: PropTypes.object,
    /** Whether to hide the Send Offer CTA */
    hideSendOfferCTA: PropTypes.bool,
};

export default DigitalRetailOverlayCTA;
